import { HGIntervalType } from "./types";
import { QuickIntervalTarget } from "./quickIntervals";
import { ConvertInterval, IntervalsRange } from "@/engine/engine";
import { countN } from "./intervals";
import { getMaxN } from "./intervalTypes";

// Coarse -> fine, main chain only, so the result is always reachable by
// HGStack.setInterval() from any current position.
const FIT_CHAIN: HGIntervalType[] = [
  "byr",
  "myr",
  "kyr",
  "c",
  // "dec",
  "y",
  "m",
  "d",
  "h",
  "min",
  "s",
];

// How many rows the widened range may span at the finest level we pick:
// coarser than that and the list becomes unusable, finer and it's just noise.
const MAX_STEPS = 200;

/**
 * Finds the interval pair to widen the range to so it covers
 * [minSecond, maxSecond].
 *
 * Preference order:
 *  1. the *coarsest* level whose ticks line up exactly with the span — a span
 *     that runs from the first second of some year to the last second of another
 *     is a run of whole years, and that is what the user asked to see, not the
 *     months inside it. Record spans land on their granularity's boundaries
 *     (a record dated "1799" ends on 1799-12-31T23:59:59), so this is really
 *     "match the granularity the records are dated at".
 *  2. failing that (records dated to odd seconds, or a whole-year span so long
 *     that year rows would overflow MAX_STEPS), the finest level at which the
 *     span still fits in MAX_STEPS ticks.
 *
 * Returns null only if the chain is empty, which cannot happen.
 */
export function fitIntervalsToSeconds(
  minSecond: bigint,
  maxSecond: bigint,
): QuickIntervalTarget | null {
  let best: QuickIntervalTarget | null = null;
  let aligned: QuickIntervalTarget | null = null;

  for (const it of FIT_CHAIN) {
    let from = ConvertInterval({ n: minSecond, it: "s" }, it).n;
    let to = ConvertInterval({ n: maxSecond, it: "s" }, it).n;

    const maxN = getMaxN(it);
    if (maxN !== undefined) {
      if (from < -maxN) from = -maxN;
      if (to > maxN) to = maxN;
    }

    // Too fine already: keep the coarser level we last settled on.
    if (best && countN(from, to) > MAX_STEPS) break;

    best = {
      interval: { n: from, it },
      endInterval: from === to ? null : { n: to, it },
    };

    // Record only the first (coarsest) level whose seconds match the span
    // exactly. Cheap to skip once found, and never worth going finer.
    if (!aligned) {
      const [spanStart, spanEnd] = IntervalsRange(
        best.interval,
        best.endInterval,
        "s",
      );
      if (spanStart === minSecond && spanEnd === maxSecond) aligned = best;
    }
  }

  return aligned ?? best;
}
